// FAQ Accordion - Smooth height animation
class FaqAccordion extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.faq-accordion-content');

    this.isAnimating = false;

    if (this.details && this.summary && this.content) {
      // Prevent native toggle so we can animate manually
      this.summary.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.toggle();
        },
        { passive: false },
      );
    }
  }

  // Get all accordion items in the same list
  getAllAccordions() {
    const accordionList = this.closest('.faq-accordion-list');
    if (!accordionList) return [];
    return Array.from(accordionList.querySelectorAll('faq-accordion'));
  }

  connectedCallback() {
    // If this accordion has the open attribute, expand it on load
    if (this.details && this.details.hasAttribute('open') && this.content) {
      // Use setTimeout to ensure DOM is fully ready
      setTimeout(() => {
        if (!this.details.open) return;
        
        const content = this.content;
        const targetHeight = content.scrollHeight;
        
        // Set initial state
        content.style.height = `${targetHeight}px`;
        content.style.opacity = '1';
        content.style.transform = 'translateY(0)';
        this.classList.add('is-open');
      }, 0);
    }
  }

  toggle() {
    if (!this.details || !this.content || this.isAnimating) return;

    if (this.details.open) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  expand() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Close all other accordions first
    const allAccordions = this.getAllAccordions();
    allAccordions.forEach((accordion) => {
      if (accordion !== this && accordion.details && accordion.details.open) {
        accordion.collapse();
      }
    });

    const content = this.content;

    // Ensure starting from collapsed state
    content.style.height = '0px';
    content.style.opacity = '0';
    content.style.transform = 'translateY(-8px)';

    // Mark as open for styling and accessibility
    this.details.open = true;
    this.classList.add('is-open');

    const targetHeight = content.scrollHeight;

    // Trigger transition in next frame
    requestAnimationFrame(() => {
      content.style.height = `${targetHeight}px`;
      content.style.opacity = '1';
      content.style.transform = 'translateY(0)';
    });

    const onTransitionEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;

      content.style.height = 'auto';
      content.removeEventListener('transitionend', onTransitionEnd);
      this.isAnimating = false;
    };

    content.addEventListener('transitionend', onTransitionEnd);
  }

  collapse() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    const content = this.content;

    // From auto height -> fixed px height so we can animate to 0
    const startHeight = content.scrollHeight;
    content.style.height = `${startHeight}px`;
    content.style.opacity = '1';
    content.style.transform = 'translateY(0)';

    // Trigger transition in next frame
    requestAnimationFrame(() => {
      content.style.height = '0px';
      content.style.opacity = '0';
      content.style.transform = 'translateY(-8px)';
    });

    const onTransitionEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;

      this.details.open = false;
      this.classList.remove('is-open');

      content.removeEventListener('transitionend', onTransitionEnd);
      this.isAnimating = false;
    };

    content.addEventListener('transitionend', onTransitionEnd);
  }
}

// Only define if not already defined
if (!customElements.get('faq-accordion')) {
  customElements.define('faq-accordion', FaqAccordion);
}
